import type {
  Connector,
  ConnectorStatus,
  ConnectorUpdateOutcome,
} from "@connector/connectorClient";
import {
  useConnectorsList,
  useUnpairConnector,
} from "@connector/connectorQueries";
import { useUser } from "@context/UserContext";
import {
  faPlugCircleXmark,
  faTriangleExclamation,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  FieldError,
  LabelTape,
  PaperPanel,
  SettingsCard,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import { formatDateTime } from "@utils/format-date";
import { relativeTime } from "@utils/relative-time";

interface ConnectorRowProps {
  connector: Connector;
  unpairing: boolean;
  onUnpair: (id: string) => void;
}

/**
 * The word each status says, and the highlighter it wears. Connected takes the
 * working (green) marker. Updating takes flag (yellow) — the same "parked and
 * waiting on someone" wash `RecurrenceCard` gives a schedule that's merely
 * paused, not ended: a machine mid-swap isn't down, it just isn't answering
 * yet. Offline goes unmarked: a machine somebody switched off is a fact, not
 * an alarm, so it reads as plain annotation.
 */
const STATUS_WORD: Record<
  ConnectorStatus,
  { label: string; tone: SwipeTone | null }
> = {
  connected: { label: "Connected", tone: "working" },
  updating: { label: "Updating…", tone: "flag" },
  offline: { label: "Offline", tone: null },
};

/**
 * How a failed auto-update reads on the row.
 *
 * Only failures appear. A successful update needs no line — the version beside
 * the status already moved, which is the whole point of BE-2273. And because
 * `lastUpdate` is the newest event, a machine that has since updated cleanly
 * stops showing the old failure on its own.
 *
 * `rollback_failed` is worded hardest deliberately: `rolled_back` is a machine
 * still running the old bundle, but a failed rollback may be a machine running
 * nothing, and those two must not read alike.
 */
const FAILED_UPDATE: Partial<Record<ConnectorUpdateOutcome, string>> = {
  rolled_back: "Update failed, rolled back",
  rollback_failed: "Update failed, rollback failed",
  download_failed: "Couldn't download update",
};

/**
 * The two in-flight outcomes read differently depending on the status standing
 * beside them, because brian only writes a terminal outcome when a cycle
 * actually finishes: an applier killed mid-swap leaves its in-flight row
 * standing forever. While brian still calls the machine Updating… the row would
 * only say that twice. Once it has stopped — its age-out window closed, so the
 * server's own verdict is that nothing is updating any more — an in-flight
 * newest event means the update was abandoned, which is the same silent
 * non-update BE-2273 was filed for and has to be said out loud.
 */
const ABANDONED_UPDATE: Partial<Record<ConnectorUpdateOutcome, string>> = {
  applying: "Update never finished",
  rolling_back: "Rollback never finished",
};

const failedUpdate = (
  status: ConnectorStatus,
  lastUpdate: Connector["lastUpdate"],
) => {
  if (!lastUpdate) return null;
  const label =
    FAILED_UPDATE[lastUpdate.outcome] ??
    (status === "updating" ? undefined : ABANDONED_UPDATE[lastUpdate.outcome]);
  if (!label) return null;
  const target = lastUpdate.toVersion;
  const text = target ? `${label} (v${target})` : label;
  const when = Date.parse(lastUpdate.occurredAt);
  const dated = !Number.isNaN(when);
  return {
    label: text,
    // `occurred_at` is the clinic box's own clock and brian bounds it on
    // neither side, so a machine running fast would put a failure that already
    // happened "in 1 hour". Clamp to now; a timestamp we can't parse at all
    // costs the timing, not the warning.
    when: dated ? relativeTime(Math.min(when, Date.now())) : null,
    // The label leads so a line truncated on a narrow pane is still readable
    // in full somewhere. Then `detail` — the download or health-gate error
    // `connectors_ctl` wrote, which is the only account of *why* — and the
    // unclamped time, for whoever is diagnosing rather than glancing.
    title: [
      text,
      lastUpdate.detail,
      dated ? formatDateTime(lastUpdate.occurredAt) : null,
    ]
      .filter(Boolean)
      .join(" — "),
  };
};

/**
 * One paired clinic computer: the machine's own label, whether Brian can reach
 * it right now, and the version it is running. The old bare status dot is gone -
 * the state is a word, and a connected machine wears the marker over it. A
 * computer somebody switched off is a fact, not an alarm, so Offline stays plain
 * annotation.
 */
const ConnectorRow = ({
  connector,
  unpairing,
  onUnpair,
}: ConnectorRowProps) => {
  const { label, tone } = STATUS_WORD[connector.status];
  const failure = failedUpdate(connector.status, connector.lastUpdate);
  return (
    <PaperPanel className="p-3" data-testid="connector-row">
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col items-start gap-1">
          {/* Hostnames and nicknames are free text, so the tape truncates rather
              than pushing the unpair control off the row. */}
          <LabelTape className="max-w-full truncate">
            {connector.name?.trim() || connector.hostname}
          </LabelTape>
          <div className="flex items-center gap-2 text-xs">
            {tone ? (
              <Swipe tone={tone}>{label}</Swipe>
            ) : (
              <span className="text-annotation">{label}</span>
            )}
            <span className="font-mono text-annotation">
              v{connector.agentVersion}
            </span>
          </div>
          {failure && (
            <div
              className="flex max-w-full min-w-0 items-center gap-1.5 text-xs"
              data-testid="connector-update-failure"
              title={failure.title}
            >
              {/* The longest label ("Update failed, rollback failed (v1.10.0)")
                  plus its time outruns a narrow settings pane, and the failure
                  is what has to survive: the time holds its width, the label
                  truncates, and the whole of it is in the row's `title`. */}
              <Swipe
                tone="stopped"
                icon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                className="min-w-0"
              >
                <span className="truncate">{failure.label}</span>
              </Swipe>
              {failure.when && (
                <span className="shrink-0 text-annotation">{failure.when}</span>
              )}
            </div>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onUnpair(connector.id)}
          busy={unpairing}
          data-testid="connector-unpair"
        >
          <FontAwesomeIcon icon={faPlugCircleXmark} aria-hidden="true" />
          {unpairing ? "Removing" : "Unpair"}
        </Button>
      </div>
    </PaperPanel>
  );
};

export const ConnectorsCard = () => {
  const { isUserAuthed, session } = useUser();
  const sessionKey = session?.userUuid ?? "";
  const connectorsQuery = useConnectorsList(sessionKey, isUserAuthed);
  const unpair = useUnpairConnector(sessionKey);

  const connectors = connectorsQuery.data ?? [];
  const loading = isUserAuthed && connectorsQuery.isLoading;
  const error = isUserAuthed && connectorsQuery.isError;

  return (
    <SettingsCard
      title="Clinic computers"
      subtitle="Each paired clinic computer and whether it's connected right now."
      data-testid="connector-list-card"
    >
      <div className="flex flex-col gap-4">
        {!isUserAuthed && (
          <p className="text-sm text-annotation">
            Select your profile to see paired clinic computers.
          </p>
        )}

        {loading && <p className="text-sm text-annotation">Checking…</p>}

        {error && (
          <FieldError data-testid="connector-list-error">
            Couldn't reach the server.
          </FieldError>
        )}

        {isUserAuthed && !loading && !error && connectors.length === 0 && (
          <p
            data-testid="connector-list-empty"
            className="text-sm text-annotation"
          >
            No clinic computers paired yet. Generate a code below and run the
            connector on the clinic computer.
          </p>
        )}

        {connectors.length > 0 && (
          <div className="flex flex-col gap-2" data-testid="connector-list">
            {connectors.map((c) => (
              <ConnectorRow
                key={c.id}
                connector={c}
                unpairing={unpair.isPending && unpair.variables === c.id}
                onUnpair={unpair.mutate}
              />
            ))}
          </div>
        )}

        {unpair.isError && (
          <FieldError data-testid="connector-unpair-error">
            Couldn't remove that connection. Try again.
          </FieldError>
        )}
      </div>
    </SettingsCard>
  );
};
